import { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Building2, ShoppingBag } from "lucide-react";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/hhsrs-guide", label: "HHSRS Guide" },
  { to: "/news", label: "News" },
  { to: "/shop", label: "Shop" },
  { to: "/contact", label: "Contact" },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="nav-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-midnight/80 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav aria-label="Main" className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center gap-2.5 group"
          onClick={() => setOpen(false)}
        >
          <Building2 className="h-7 w-7 text-gold" strokeWidth={1.5} />
          <span className="font-heading font-black text-xl tracking-tight text-white">
            O Law Ventures<span className="text-gold"> Ltd</span>
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-6">
          {links.map((l) =>
            l.to === "/shop" ? (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className="shop-pulse relative inline-flex items-center gap-2 bg-gold hover:bg-gold-hover text-midnight font-semibold text-sm uppercase tracking-[0.1em] px-5 py-2 rounded-full transition-colors duration-200"
              >
                <ShoppingBag className="h-4 w-4" strokeWidth={2} />
                {l.label}
                <span className="absolute -top-2 -right-2 bg-midnight text-gold text-[0.6rem] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full border border-gold/50">
                  New
                </span>
              </NavLink>
            ) : (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-sm uppercase tracking-[0.1em] font-medium transition-colors duration-200 ${
                    isActive ? "text-gold" : "text-gray-300 hover:text-white"
                  }`
                }
              >
                {l.label}
              </NavLink>
            )
          )}
        </div>

        <Link
          to="/contact"
          data-testid="nav-cta-button"
          className="hidden lg:inline-flex items-center bg-gold hover:bg-gold-hover text-midnight font-semibold text-sm uppercase tracking-[0.12em] px-6 py-3 transition-colors duration-300"
        >
          Get in touch
        </Link>

        <button
          data-testid="nav-mobile-toggle"
          className="lg:hidden text-white"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
        </button>
      </nav>

      {open && (
        <div
          data-testid="nav-mobile-menu"
          className="lg:hidden bg-midnight/95 backdrop-blur-xl border-t border-white/10 px-6 py-6 flex flex-col gap-5"
        >
          {links.map((l) =>
            l.to === "/shop" ? (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                className="inline-flex items-center justify-center gap-2 bg-gold text-midnight font-semibold text-base uppercase tracking-[0.15em] px-6 py-3 rounded-full w-fit"
              >
                <ShoppingBag className="h-4 w-4" strokeWidth={2} />
                {l.label}
              </NavLink>
            ) : (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-base uppercase tracking-[0.15em] font-medium ${
                    isActive ? "text-gold" : "text-gray-200"
                  }`
                }
              >
                {l.label}
              </NavLink>
            )
          )}
          <Link
            to="/contact"
            onClick={() => setOpen(false)}
            className="bg-gold text-midnight font-semibold text-center uppercase tracking-[0.12em] px-6 py-3 mt-2"
          >
            Get in touch
          </Link>
        </div>
      )}
    </header>
  );
};

export default Navbar;
